import { useEffect, useMemo, useState } from "react"
import SimpleBackdropLoader from "../BackdropLoading/BackdropLoading"
import Flatpickr from "react-flatpickr"
import { useRoutePermission } from "../../context/RoutePermissionContext"
import { getEntityIdFromURL, getSlugFromURL } from "../../utils"
import { BASE_URL } from "../Alerts/Alerts"
import { FetchHelper } from "../../utils/fetchHelper"
import { showError, showSuccess } from "../../utils/snackbar"
import { evaluateFormula } from "../../utils/formulaEvaluator"
import { useLocalization } from "../../providers/LocalizationProvider"
import FormPdfDownloadButton from "./FormPdfDownloadButton";

const OTHER_OPTION_VALUE = "__other__"

const getOtherFieldKey = (fieldKey) => `${fieldKey}__other`

const getFieldOptions = (field) =>
    (field?.options || [])
        .map((opt) => (typeof opt === "string" ? opt : opt?.value))
        .filter(Boolean)


        const isPrimitiveEmpty = (v) =>
    v === undefined ||
    v === null ||
    v === "" ||
    (Array.isArray(v) && v.length === 0)

const isAnswered = (pickedKey, formState, fields) => {
    if (pickedKey.includes(".")) {
        const [gridKey, rowKey] = pickedKey.split(".")
        const gridVal = formState[gridKey] || {}

        return !isPrimitiveEmpty(gridVal[rowKey])
    }

    const field = fields.find((f) => f.key === pickedKey)
    const value = formState[pickedKey]

    if (field?.type === "radio-grid" && field.rows?.length) {
        const gridVal = value || {}

        return field.rows.every((r) => !isPrimitiveEmpty(gridVal[r.key]))
    }

    return !isPrimitiveEmpty(value)
}

const getGroupStatus = (field, formState, fields) => {
    const picked = field.picked_keys || []
    const min = Math.max(0, field.min_required || 0)

    const answered = picked.reduce(
        (acc, key) =>
            acc + (isAnswered(key, formState, fields) ? 1 : 0),
        0,
    )

    return {
        answered,
        total: picked.length,
        min,
        passes: min === 0 || answered >= min,
    }
}
const DynamicDetailFormModal = ({ open, onClose, formItem, setRefreshKey, embedded = false, entityIdOverride = null, onValidityChange = null, pdfMeta = {} }) => {
    const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB
    const fields = formItem?.schema?.fields || []
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isPdfDownloading, setIsPdfDownloading] = useState(false)
    const submissionData = useMemo(
        () => (formItem?.allow_multiple ? {} : formItem?.submission?.data || {}),
        [formItem?.allow_multiple, formItem?.submission?.data],
    )
    const [formState, setFormState] = useState({})
    const [errors, setErrors] = useState({})
    const { canManage: canManageRouteActions } = useRoutePermission()
    const { voiceiiTags } = useLocalization()
    const mode = formItem?.mode || "add"
    // Disable form interaction in view mode
    const isDisabled = mode === "view"

    const initialForm = useMemo(() => {
        const obj = {}

        fields.forEach((f) => {
            if (!f?.key) return

            const optionValues = getFieldOptions(f)

            if (f.type === "multi-select") {
                const currentValues = Array.isArray(submissionData?.[f.key])
                    ? submissionData[f.key]
                    : submissionData?.[f.key]
                      ? [submissionData[f.key]]
                      : []

                const selectedOptions = currentValues.filter((item) => optionValues.includes(item))
                const otherValues = currentValues.filter((item) => !optionValues.includes(item))

                obj[f.key] = otherValues.length
                    ? [...selectedOptions, OTHER_OPTION_VALUE]
                    : selectedOptions
                obj[getOtherFieldKey(f.key)] = otherValues[0] ?? ""
            } else if (f.type === "files" || f.type === "images") {
                const val = submissionData?.[f.key]

                obj[f.key] = Array.isArray(val) ? val : val ? [val] : []
            } else if (f.type === "file" || f.type === "image") {
                obj[f.key] = submissionData?.[f.key] ?? null
            } else if (f.type === "checkbox") {
                obj[f.key] = submissionData?.[f.key] ?? false
            } 
            else if (f.type === "slider") {
                obj[f.key] =
                    submissionData?.[f.key] ??
                    (f.default_value !== undefined
                        ? Number(f.default_value)
                        : f.min ?? 0)
            }
            else if (f.type === "computed" || f.type === "number") {
                obj[f.key] = submissionData?.[f.key] ?? null
            } else if (f.type === "radio-grid") {
                obj[f.key] = submissionData?.[f.key] ?? {}
            } else if ((f.type === "select" || f.type === "radio") && f.allow_other) {
                const currentValue = submissionData?.[f.key] ?? ""

                if (currentValue && !optionValues.includes(currentValue)) {
                    obj[f.key] = OTHER_OPTION_VALUE
                    obj[getOtherFieldKey(f.key)] = currentValue
                } else {
                    obj[f.key] = currentValue
                    obj[getOtherFieldKey(f.key)] = submissionData?.[getOtherFieldKey(f.key)] ?? ""
                }
            } else {
                obj[f.key] = submissionData?.[f.key] ?? ""
            }
        })

        return obj
    }, [fields, submissionData])

    useEffect(() => {
        if (open) {
            setFormState(initialForm)
            setErrors({})
        }
    }, [open, initialForm])

    const validateForm = () => {
        if (isDisabled) return true
        const newErrors = {}

        fields.forEach((field) => {
            if (!field?.key) return

            const value = formState[field.key]
            const otherValue = formState[getOtherFieldKey(field.key)] || ""


               // answer-group-rule always validate
                if (field.type === "answer-group-rule") {
                    const status = getGroupStatus(field, formState, fields)

                    if (!status.passes) {
                        newErrors[field.key] =
                            field.description_key ||
                            voiceiiTags(
                                `Please answer at least ${status.min} of ${status.total} selected questions`
                            )
                    }

                    return
                }

                // Remaining validations only for required fields
            if (!field.required) return

            // text / textarea / select / radio
            if (
                field.type !== "multi-select" &&
                field.type !== "files" &&
                field.type !== "radio-grid" &&
                (value === undefined ||
                    value === null ||
                    (typeof value === "string" && value.trim() === "") ||
                    (typeof value === "number" && isNaN(value)))
            ) {
                newErrors[field.key] = `${field.label_key || field.key} ${voiceiiTags("is required")}`
            }

            // multi-select
            if (field.type === "multi-select" && (!value || value.length === 0)) {
                newErrors[field.key] = `${field.label_key || field.key} ${voiceiiTags("is required")}`
            }

            if (
                field.allow_other &&
                (field.type === "select" || field.type === "radio") &&
                value === OTHER_OPTION_VALUE &&
                !String(otherValue).trim()
            ) {
                newErrors[getOtherFieldKey(field.key)] =
                    `${voiceiiTags("Other")} ${field.label_key || field.key} ${voiceiiTags("is required")}`
            }

            if (
                field.allow_other &&
                field.type === "multi-select" &&
                Array.isArray(value) &&
                value.includes(OTHER_OPTION_VALUE) &&
                !String(otherValue).trim()
            ) {
                newErrors[getOtherFieldKey(field.key)] =
                    `${voiceiiTags("Other")} ${field.label_key || field.key} ${voiceiiTags("is required")}`
            }


            // files
            if (field.type === "files" || field.type === "images") {
                if (!value || value.length === 0) {
                    newErrors[field.key] = `${field.label_key || field.key} ${voiceiiTags("is required")}`
                } else {
                    for (const file of value) {
                        if (file instanceof File && file.size > MAX_FILE_SIZE) {
                            newErrors[field.key] = voiceiiTags("File size must be less than 5MB")
                            break
                        }
                    }
                }
            }

            if (field.type === "file" || field.type === "image") {
                if (!value) {
                    newErrors[field.key] = `${field.label_key || field.key} ${voiceiiTags("is required")}`
                } else {
                    if (value instanceof File && value.size > MAX_FILE_SIZE) {
                        newErrors[field.key] = voiceiiTags("File size must be less than 5MB")
                    }
                }
            }

            if (field.type === "radio-grid") {
                const rows = field.rows || []
                const gridValue = value || {}

                const hasMissing = rows.some((row) => !gridValue[row.key])

                if (hasMissing) {
                    newErrors[field.key] = `${field.label_key || field.key} ${voiceiiTags("is required")}`
                }
            }
        })

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const isFormInvalid = useMemo(() => {
        if (isDisabled) return false
        return fields.some((field) => {

            if (field.type === "answer-group-rule") {
                return !getGroupStatus(field, formState, fields).passes
            }
            if (!field.required || !field.key) return false

            const value = formState[field.key]

            if (
                field.type === "multi-select" ||
                field.type === "files" ||
                field.type === "images"
            ) {
                return !value || value.length === 0
            }
            if (field.type === "image" || field.type === "file") {
                return !value
            }
            if (
                field.allow_other &&
                (field.type === "select" || field.type === "radio") &&
                value === OTHER_OPTION_VALUE
            ) {
                return !String(formState[getOtherFieldKey(field.key)] || "").trim()
            }
            if (
                field.allow_other &&
                field.type === "multi-select" &&
                Array.isArray(value) &&
                value.includes(OTHER_OPTION_VALUE)
            ) {
                return !String(formState[getOtherFieldKey(field.key)] || "").trim()
            }
            if (field.type === "radio-grid") {
                const rows = field.rows || []
                const gridValue = value || {}

                return rows.some((row) => !gridValue[row.key])
            }

            return (
                value === "" ||
                value === null ||
                value === undefined ||
                (typeof value === "number" && isNaN(value))
            )
        })
    }, [fields, formState])

    useEffect(() => {
        onValidityChange?.(isFormInvalid)
    }, [isFormInvalid, onValidityChange])

    const handleChange = (key, value) => {
        if (isDisabled) return
        setFormState((prev) => ({
            ...prev,
            [key]: value,
        }))
        setErrors((prev) => {
            if (!prev[key]) return prev
            const copy = { ...prev }
            delete copy[key]
            return copy
        })
    }

    const handleRadioGridChange = (fieldKey, rowKey, colValue) => {
        const current = formState[fieldKey] || {}

        handleChange(fieldKey, {
            ...current,
            [rowKey]: colValue,
        })
    }

    const handleMultiSelect = (key, option) => {
        const current = formState[key] || []
        const exists = current.includes(option)

        handleChange(key, exists ? current.filter((o) => o !== option) : [...current, option])
    }

    // The form-reference-number field (if the schema has one) doubles as the PDF's ref number.
    const refNumberFromSchema = useMemo(() => {
        const refField = fields.find((f) => f?.type === "form-reference-number" && f?.key)
        if (!refField) return undefined
        return formState?.[refField.key] || refField.default_value || refField.placeholder_key || undefined
    }, [fields, formState])

    // Best-effort auto-detection for the header fields (patient name, org name, date).
    // These paths are guesses based on common shapes — pass the real values via the
    // `pdfMeta` prop from the parent screen (which has the actual patient/org context)
    // and they'll take priority over anything auto-detected here.
    const autoDetectedPatientName =
        formItem?.entity_name ||
        formItem?.patient_name ||
        formItem?.entity?.name ||
        formItem?.patient?.name ||
        formItem?.submission?.entity_name ||
        undefined

    const pdfDownloadMeta = {
        refNumber: refNumberFromSchema,
        patientName: autoDetectedPatientName,
        date: new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }),
        ...pdfMeta,
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        if (isDisabled || !canManageRouteActions) return

        const isValid = validateForm()
        if (!isValid) return

        const formData = new FormData()

        formData.append("slug", getSlugFromURL())
        formData.append("entity_id", entityIdOverride || getEntityIdFromURL())
        formData.append("function_name", "save_patient_form")
        formData.append("form_slug", formItem?.slug)

        const normalData = {}

        for (const field of fields) {
            const key = field.key
            if (!key) continue

            const value = formState[key]
            const otherValue = String(formState[getOtherFieldKey(key)] || "").trim()

            // ===== FILES =====
            if (field.type === "files" || field.type === "images") {
                const existingFiles = []

                const fileList = Array.isArray(value) ? value : value ? [value] : []

                fileList.forEach((item) => {
                    // ✅ NEW FILE
                    if (item instanceof File) {
                        formData.append(key, item)
                    }

                    // ✅ EXISTING FILE
                    else {
                        existingFiles.push(item)
                    }
                })

                if (existingFiles.length) {
                    normalData[key] = existingFiles
                    // formData.append(key, JSON.stringify(existingFiles))
                }
            } else if (field.type === "file" || field.type === "image") {
                if (value instanceof File) {
                    formData.append(key, value)
                } else {
                    normalData[key] = value || ""
                }
            } else if ((field.type === "select" || field.type === "radio") && field.allow_other) {
                normalData[key] = value === OTHER_OPTION_VALUE ? otherValue : value ?? ""
            } else if (field.type === "multi-select" && field.allow_other) {
                const selectedValues = Array.isArray(value) ? value : []

                normalData[key] = selectedValues
                    .filter((item) => item !== OTHER_OPTION_VALUE)
                    .concat(
                        selectedValues.includes(OTHER_OPTION_VALUE) && otherValue
                            ? [otherValue]
                            : [],
                    )
            } else {
                normalData[key] = value ?? (field.type === "multi-select" ? [] : "")
            }
        }

        // normal fields ek JSON me bhej
        formData.append("form_data", JSON.stringify(normalData))
        setIsSubmitting(true)

        try {
            const resp = await FetchHelper.postFormData("api/v1/asset-detail/extension", formData)

            if (resp?.data?.status) {
                showSuccess(resp?.data?.message || voiceiiTags("Form submitted successfully"))
                onClose?.()
            } else {
                showError(resp?.data?.message || voiceiiTags("Something went wrong"))
            }
        } catch (err) {
            console.warn("DynamicDetailFormModal error", err)

            showError(err?.response?.data?.message || err?.message || voiceiiTags("Failed to submit form"))
        } finally {
            setIsSubmitting(false)
            setRefreshKey((prev) => prev + 1)
        }
    }

    useEffect(() => {
        const computedFields = fields.filter((f) => f.type === "computed")
        if (!computedFields.length) return

        let hasChange = false
        const updated = {}

        computedFields.forEach((field) => {
            const result = evaluateFormula(field.formula, formState)

            if (formState[field.key] !== result) {
                updated[field.key] = result
                hasChange = true
            }
        })

        if (hasChange) {
            setFormState((prev) => ({
                ...prev,
                ...updated,
            }))
        }
    }, [formState, fields])
    if (!open || !formItem) return null

    // When embedded inside a parent modal (e.g., onboarding wizard), render without overlay
    const formContent = (
                    <form
                        id={embedded ? "onboarding-step-form" : undefined}
                        className="e-dynamic-detail-form-modal-form"
                        onSubmit={handleSubmit}
                    >
                        <div className={isDisabled ? "e-dynamic-detail-form-modal-form-view" : ""}>
                        {fields.map((field, idx) => {
                            if (!field?.key && field.type !== "text") return null

                            const value = formState[field.key]
                            const isImageField = field.type === "image" || field.type === "images"

                            const isMultiple = field.type === "files" || field.type === "images"
                            const allowedFileText = isImageField
                                ? "JPG, PNG, WEBP images only"
                                : "PDF, Word, Excel, CSV and images"

                            return (
                                <div key={idx} className="e-dynamic-detail-form-modal-field">
                                    {field.type !== "section-header" &&
                                        field.type !== "form-reference-number" &&
                                        field.label_key && (
                                            <label className="e-dynamic-detail-form-modal-label">
                                                {field.label_key}
                                                {field.required && (
                                                    <span className="e-dynamic-detail-form-modal-required">
                                                        *
                                                    </span>
                                                )}
                                            </label>
                                        )}

                                    {/* TEXTAREA */}
                                    {field.type === "textarea" && (
                                        <textarea
                                            className="e-dynamic-detail-form-modal-textarea"
                                            value={value || ""}
                                            readOnly={isDisabled}
                                            disabled={isDisabled || !canManageRouteActions}
                                            onChange={(e) =>
                                                handleChange(field.key, e.target.value)
                                            }
                                        />
                                    )}

                                    {/* CHECKBOX */}
                                    {field.type === "checkbox" && (
                                        <label className="e-dynamic-detail-form-modal-checkbox-single">
                                            <input
                                                type="checkbox"
                                                checked={!!value}
                                                readOnly={isDisabled}
                                                disabled={isDisabled || !canManageRouteActions}
                                                onChange={(e) =>
                                                    handleChange(field.key, e.target.checked)
                                                }
                                            />

                                            <span className="e-dynamic-detail-form-modal-checkbox-custom"></span>

                                            <span className="e-dynamic-detail-form-modal-checkbox-label">
                                                {field.title_key || field.label_key}
                                            </span>
                                        </label>
                                    )}

                                    {/* TEXT */}
                                    {field.type === "text" && (
                                        <input
                                            type="text"
                                            className="e-dynamic-detail-form-modal-input"
                                            value={value || ""}
                                            readOnly={isDisabled}
                                            disabled={isDisabled || !canManageRouteActions}
                                            onChange={(e) =>
                                                handleChange(field.key, e.target.value)
                                            }
                                        />
                                    )}

                                    {/* FORM REFERENCE NUMBER */}
                                    {field.type === "form-reference-number" && (
                                        <div className="e-dynamic-detail-form-modal-reference-box">
                                            {field.title_key && (
                                                <div className="e-dynamic-detail-form-modal-reference-title">
                                                    {field.title_key}
                                                </div>
                                            )}

                                            <div className="e-dynamic-detail-form-modal-reference-content">
                                                <span className="e-dynamic-detail-form-modal-reference-label">
                                                    {field.label_key || voiceiiTags("Form Ref No.")}
                                                </span>

                                                <span className="e-dynamic-detail-form-modal-reference-value">
                                                    {value ||
                                                        field.default_value ||
                                                        field.placeholder_key ||
                                                        "—"}
                                                </span>
                                            </div>
                                        </div>
                                    )}

                                    {/* SELECT */}
                                    {field.type === "select" && (
                                        <>
                                            <select
                                                className="e-dynamic-detail-form-modal-select"
                                                value={value || ""}
                                                readOnly={isDisabled}
                                                disabled={isDisabled || !canManageRouteActions}
                                                onChange={(e) => {
                                                    const nextValue = e.target.value

                                                    handleChange(field.key, nextValue)

                                                    if (nextValue !== OTHER_OPTION_VALUE) {
                                                        handleChange(
                                                            getOtherFieldKey(field.key),
                                                            "",
                                                        )
                                                    }
                                                }}
                                            >
                                                <option value="">{voiceiiTags("Select")}</option>
                                                {field.options?.map((opt) => {
                                                    const optValue =
                                                        typeof opt === "string" ? opt : opt.value
                                                    const optLabel =
                                                        typeof opt === "string"
                                                            ? opt
                                                            : opt.label_key ||
                                                              opt.label ||
                                                              opt.value

                                                    return (
                                                        <option key={optValue} value={optValue}>
                                                            {optLabel}
                                                        </option>
                                                    )
                                                })}
                                                {field.allow_other && (
                                                    <option value={OTHER_OPTION_VALUE}>
                                                        Other
                                                    </option>
                                                )}
                                            </select>

                                            {field.allow_other && value === OTHER_OPTION_VALUE && (
                                                <input
                                                    type="text"
                                                    className={`e-dynamic-detail-form-modal-input mt-2 ${
                                                        errors[getOtherFieldKey(field.key)]
                                                            ? "border-danger"
                                                            : ""
                                                    }`}
                                                    value={
                                                        formState[getOtherFieldKey(field.key)] || ""
                                                    }
                                                    placeholder={voiceiiTags("Please specify")}
                                                    readOnly={isDisabled}
                                                    disabled={isDisabled || !canManageRouteActions}
                                                    onChange={(e) =>
                                                        handleChange(
                                                            getOtherFieldKey(field.key),
                                                            e.target.value,
                                                        )
                                                    }
                                                />
                                            )}
                                        </>
                                    )}

                                    {/* RADIO */}
                                    {field.type === "radio" && (
                                        <>
                                            <div className="e-dynamic-detail-form-modal-radio-group">
                                                {field.options?.map((opt) => {
                                                    const optValue =
                                                        typeof opt === "string" ? opt : opt.value
                                                    const optLabel =
                                                        typeof opt === "string"
                                                            ? opt
                                                            : opt.label_key ||
                                                              opt.label ||
                                                              opt.value

                                                    return (
                                                        <label
                                                            key={optValue}
                                                            className="e-dynamic-detail-form-modal-radio"
                                                        >
                                                            <input
                                                                type="radio"
                                                                name={field.key}
                                                                value={optValue}
                                                                checked={value === optValue}
                                                                readOnly={isDisabled}
                                                                disabled={
                                                                    isDisabled ||
                                                                    !canManageRouteActions
                                                                }
                                                                onChange={() => {
                                                                    handleChange(
                                                                        field.key,
                                                                        optValue,
                                                                    )

                                                                    if (
                                                                        optValue !==
                                                                        OTHER_OPTION_VALUE
                                                                    ) {
                                                                        handleChange(
                                                                            getOtherFieldKey(
                                                                                field.key,
                                                                            ),
                                                                            "",
                                                                        )
                                                                    }
                                                                }}
                                                            />
                                                            {optLabel}
                                                        </label>
                                                    )
                                                })}

                                                {field.allow_other && (
                                                    <label className="e-dynamic-detail-form-modal-radio">
                                                        <input
                                                            type="radio"
                                                            name={field.key}
                                                            value={OTHER_OPTION_VALUE}
                                                            checked={value === OTHER_OPTION_VALUE}
                                                            readOnly={isDisabled}
                                                            disabled={
                                                                isDisabled || !canManageRouteActions
                                                            }
                                                            onChange={() =>
                                                                handleChange(
                                                                    field.key,
                                                                    OTHER_OPTION_VALUE,
                                                                )
                                                            }
                                                        />
                                                        Other
                                                    </label>
                                                )}
                                            </div>

                                            {field.allow_other && value === OTHER_OPTION_VALUE && (
                                                <input
                                                    type="text"
                                                    className={`e-dynamic-detail-form-modal-input mt-2 ${
                                                        errors[getOtherFieldKey(field.key)]
                                                            ? "border-danger"
                                                            : ""
                                                    }`}
                                                    value={
                                                        formState[getOtherFieldKey(field.key)] || ""
                                                    }
                                                    placeholder={voiceiiTags("Please specify")}
                                                    readOnly={isDisabled}
                                                    disabled={isDisabled || !canManageRouteActions}
                                                    onChange={(e) =>
                                                        handleChange(
                                                            getOtherFieldKey(field.key),
                                                            e.target.value,
                                                        )
                                                    }
                                                />
                                            )}
                                        </>
                                    )}

                                    {/* MULTI SELECT */}
                                    {field.type === "multi-select" && (
                                        <>
                                            <div className="e-dynamic-detail-form-modal-checkbox-group">
                                                {field.options?.map((opt) => {
                                                    const optValue =
                                                        typeof opt === "string" ? opt : opt.value
                                                    const optLabel =
                                                        typeof opt === "string"
                                                            ? opt
                                                            : opt.label_key ||
                                                              opt.label ||
                                                              opt.value

                                                    return (
                                                        <label
                                                            key={optValue}
                                                            className="e-dynamic-detail-form-modal-checkbox"
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={(value || []).includes(
                                                                    optValue,
                                                                )}
                                                                readOnly={isDisabled}
                                                                disabled={
                                                                    isDisabled ||
                                                                    !canManageRouteActions
                                                                }
                                                                onChange={() =>
                                                                    handleMultiSelect(
                                                                        field.key,
                                                                        optValue,
                                                                    )
                                                                }
                                                            />
                                                            {optLabel}
                                                        </label>
                                                    )
                                                })}

                                                {field.allow_other && (
                                                    <label className="e-dynamic-detail-form-modal-checkbox">
                                                        <input
                                                            type="checkbox"
                                                            checked={(value || []).includes(
                                                                OTHER_OPTION_VALUE,
                                                            )}
                                                            readOnly={isDisabled}
                                                            disabled={
                                                                isDisabled || !canManageRouteActions
                                                            }
                                                            onChange={() => {
                                                                const currentValues = Array.isArray(
                                                                    value,
                                                                )
                                                                    ? value
                                                                    : []
                                                                const exists =
                                                                    currentValues.includes(
                                                                        OTHER_OPTION_VALUE,
                                                                    )

                                                                handleChange(
                                                                    field.key,
                                                                    exists
                                                                        ? currentValues.filter(
                                                                              (item) =>
                                                                                  item !==
                                                                                  OTHER_OPTION_VALUE,
                                                                          )
                                                                        : [
                                                                              ...currentValues,
                                                                              OTHER_OPTION_VALUE,
                                                                          ],
                                                                )

                                                                if (exists) {
                                                                    handleChange(
                                                                        getOtherFieldKey(field.key),
                                                                        "",
                                                                    )
                                                                }
                                                            }}
                                                        />
                                                        Other
                                                    </label>
                                                )}
                                            </div>

                                            {field.allow_other &&
                                                Array.isArray(value) &&
                                                value.includes(OTHER_OPTION_VALUE) && (
                                                    <input
                                                        type="text"
                                                        className={`e-dynamic-detail-form-modal-input mt-2 ${
                                                            errors[getOtherFieldKey(field.key)]
                                                                ? "border-danger"
                                                                : ""
                                                        }`}
                                                        value={
                                                            formState[
                                                                getOtherFieldKey(field.key)
                                                            ] || ""
                                                        }
                                                        placeholder={voiceiiTags("Please specify")}
                                                        readOnly={isDisabled}
                                                        disabled={
                                                            isDisabled || !canManageRouteActions
                                                        }
                                                        onChange={(e) =>
                                                            handleChange(
                                                                getOtherFieldKey(field.key),
                                                                e.target.value,
                                                            )
                                                        }
                                                    />
                                                )}
                                        </>
                                    )}

                                    {field.type === "radio-grid" && (
                                        <div className="mb-3">
                                            <label className="fw-bold mb-2 d-block">
                                                {field.label_key}
                                                {field.required && (
                                                    <span className="text-danger ms-1">*</span>
                                                )}
                                            </label>

                                            <div
                                                className={`border ${errors[field.key] ? "border-danger" : ""}`}
                                                style={{ overflowX: "auto" }}
                                            >
                                                <table className="table table-bordered mb-0">
                                                    <thead className="table-light">
                                                        <tr>
                                                            <th style={{ minWidth: "250px" }}>
                                                                Question
                                                            </th>
                                                            {field.column_headers?.map((col) => (
                                                                <th
                                                                    key={col.value}
                                                                    className="text-center"
                                                                >
                                                                    <div className="fw-semibold">
                                                                        {col.label_key}
                                                                    </div>
                                                                    {col.description_key && (
                                                                        <div
                                                                            className="text-muted"
                                                                            style={{
                                                                                fontSize: "12px",
                                                                            }}
                                                                        >
                                                                            {col.description_key}
                                                                        </div>
                                                                    )}
                                                                </th>
                                                            ))}
                                                        </tr>
                                                    </thead>

                                                    <tbody>
                                                        {(field.rows || []).map((row, idx) => (
                                                            <tr key={row.key}>
                                                                <td>
                                                                    <div className="fw-medium">
                                                                        {row.label_key}
                                                                    </div>
                                                                    {row.description_key && (
                                                                        <div
                                                                            className="text-muted"
                                                                            style={{
                                                                                fontSize: "12px",
                                                                            }}
                                                                        >
                                                                            {row.description_key}
                                                                        </div>
                                                                    )}
                                                                </td>

                                                                {field.column_headers?.map(
                                                                    (col) => (
                                                                        <td
                                                                            key={col.value}
                                                                            className="text-center"
                                                                        >
                                                                            <input
                                                                                type="radio"
                                                                                name={`${field.key}-${row.key}`}
                                                                                value={col.value}
                                                                                checked={
                                                                                    (value || {})[
                                                                                        row.key
                                                                                    ] === col.value
                                                                                }
                                                                                disabled={
                                                                                    isDisabled ||
                                                                                    !canManageRouteActions
                                                                                }
                                                                                onChange={() =>
                                                                                    handleRadioGridChange(
                                                                                        field.key,
                                                                                        row.key,
                                                                                        col.value,
                                                                                    )
                                                                                }
                                                                            />
                                                                        </td>
                                                                    ),
                                                                )}
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}

                                    {/* DATE */}
                                    {field.type === "date" && (
                                        <div className="position-relative">
                                            <Flatpickr
                                                value={value || null}
                                                onChange={(dates) => {
                                                    if (dates?.[0]) {
                                                        const d = dates[0]
                                                        const year = d.getFullYear()
                                                        const month = String(d.getMonth() + 1).padStart(2, "0")
                                                        const day = String(d.getDate()).padStart(2, "0")
                                                        handleChange(field.key, `${year}-${month}-${day}`)
                                                    } else {
                                                        handleChange(field.key, "")
                                                    }
                                                }}
                                                options={{
                                                    dateFormat: "d/m/Y",
                                                    allowInput: true,
                                                    clickOpens: true,
                                                    parseDate: (dateStr) => {
                                                        const parts = dateStr.split("-").map(Number)
                                                        return new Date(parts[0], parts[1] - 1, parts[2])
                                                    },
                                                }}
                                                render={({ value, ...props }, ref) => (
                                                    <input
                                                        {...props}
                                                        ref={ref}
                                                        value={value || ""}
                                                        placeholder={voiceiiTags("Select date")}
                                                        className={`e-dynamic-detail-form-modal-input pe-5 ${
                                                            errors[field.key] ? "border-danger" : ""
                                                        }`}
                                                        readOnly={isDisabled}
                                                        disabled={
                                                            isDisabled || !canManageRouteActions
                                                        }
                                                    />
                                                )}
                                            />

                                            {/* Calendar Icon */}
                                            <span
                                                className="position-absolute"
                                                style={{
                                                    right: "12px",
                                                    top: "50%",
                                                    transform: "translateY(-50%)",
                                                    pointerEvents: "none",
                                                    fontSize: "18px",
                                                    color: "#888",
                                                }}
                                            >
                                                📅
                                            </span>
                                        </div>
                                    )}

                                    {/* NUMBER */}
                                    {field.type === "number" && (
                                        <input
                                            type="number"
                                            className={`e-dynamic-detail-form-modal-input ${
                                                errors[field.key] ? "border-danger" : ""
                                            }`}
                                            value={value ?? ""}
                                            min={field.min}
                                            max={field.max}
                                            readOnly={isDisabled}
                                            disabled={isDisabled || !canManageRouteActions}
                                            onChange={(e) =>
                                                handleChange(
                                                    field.key,
                                                    e.target.value ? Number(e.target.value) : null,
                                                )
                                            }
                                        />
                                    )}

                                {field.type === "slider" && (() => {
                                        const min = field.min ?? 0
                                        const max = field.max ?? 100
                                        const step = field.step ?? 1

                                        const current = Number(
                                            value ??
                                            field.default_value ??
                                            min
                                        )

                                        const range = max - min

                                        const rawPercentage =
                                            range > 0
                                                ? ((current - min) * 100) / range
                                                : 100

                                        const percentage = Math.min(
                                            100,
                                            Math.max(0, rawPercentage),
                                        )

                                        return (
                                            <div className="e-slider-wrapper">

                                                <div className="e-slider-badge">
                                                    {current}
                                                </div>

                                                <div className="e-slider-main">

                                                    <span className="e-slider-limit w-0">
                                                        {min}
                                                    </span>

                                                    <input
                                                        type="range"
                                                        min={min}
                                                        max={max}
                                                        step={step}
                                                        value={current}
                                                        disabled={
                                                            isDisabled ||
                                                            !canManageRouteActions
                                                        }
                                                        onChange={(e) =>
                                                            handleChange(
                                                                field.key,
                                                                Number(e.target.value),
                                                            )
                                                        }
                                                        className="e-slider"
                                                        style={{
                                                            background: `linear-gradient(to right,#0d6efd 0%,#0d6efd ${percentage}%,#dee2e6 ${percentage}%,#dee2e6 100%)`,
                                                        }}
                                                    />

                                                    <span className="e-slider-limit">
                                                        {max}
                                                    </span>

                                                </div>

                                            </div>
                                        )
                                    })()}

                                    {field.type === "answer-group-rule" && (() => {

                                        const status = getGroupStatus(
                                            field,
                                            formState,
                                            fields,
                                        )

                                        const invalid =
                                            errors[field.key] || !status.passes

                                        const complete =
                                            status.passes && status.min > 0

                                        return (
                                            <div
                                                className={`e-answer-group-rule
                                                ${
                                                    invalid
                                                        ? "e-answer-group-rule-error"
                                                        : complete
                                                        ? "e-answer-group-rule-success"
                                                        : ""
                                                }`}
                                            >

                                                <div className="e-answer-group-rule-icon">
                                                    {invalid ? "⚠" : "✔"}
                                                </div>

                                                <div>

                                                    <div className="fw-semibold">
                                                        {field.label_key ||
                                                            `Answer at least ${status.min} of ${status.total} selected questions`}
                                                    </div>

                                                    <div className="small text-muted">
                                                        Answered {status.answered} of {status.total}
                                                        {" · "}
                                                        minimum {status.min} required
                                                    </div>

                                                    {field.description_key && (
                                                        <div className="small text-muted">
                                                            {field.description_key}
                                                        </div>
                                                    )}

                                                </div>

                                            </div>
                                        )
                                    })()}

                                    {field.type === "computed" && (
                                        <div className="mb-3">
                                            <label className="fw-bold mb-1 d-block">
                                                {field.label_key}
                                            </label>

                                            <div className="border px-3 py-2 bg-light fw-bold">
                                                {value !== null && value !== undefined
                                                    ? value
                                                    : "—"}
                                            </div>
                                        </div>
                                    )}

                                    {field.type === "section-header" && (
                                        <div className="pt-1 pb-1">
                                            <div className="border-bottom pb-2">
                                                <h5 className="fw-bold mb-1">{field.label_key}</h5>
                                                {field.description_key && (
                                                    <p
                                                        className="text-muted mb-0"
                                                        style={{ fontSize: "14px" }}
                                                    >
                                                        {field.description_key}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {(field.type === "file" ||
                                        field.type === "files" ||
                                        field.type === "image" ||
                                        field.type === "images") && (
                                        <div className="e-dynamic-detail-form-modal-file-wrapper">
                                            {/* Dropzone */}
                                            {!isDisabled && (
                                                <div
                                                    className="e-dynamic-detail-form-modal-dropzone"
                                                    onClick={() => {
                                                        if (!canManageRouteActions) return
                                                        document
                                                            .getElementById(`file-${field.key}`)
                                                            .click()
                                                    }}
                                                >
                                                    <input
                                                        id={`file-${field.key}`}
                                                        type="file"
                                                        multiple={isMultiple}
                                                        accept={
                                                            isImageField
                                                                ? "image/*"
                                                                : ".pdf,.doc,.docx,.xls,.xlsx,.csv,image/*"
                                                        }
                                                        className="e-dynamic-detail-form-modal-file-input-hidden"
                                                        disabled={!canManageRouteActions}
                                                        onChange={(e) => {
                                                            if (!canManageRouteActions) return

                                                            const files = Array.from(
                                                                e.target.files || [],
                                                            )

                                                            const ALLOWED_FILE_TYPES = isImageField
                                                                ? [
                                                                      "image/jpeg",
                                                                      "image/png",
                                                                      "image/webp",
                                                                      "image/jpg",
                                                                  ]
                                                                : [
                                                                      "application/pdf",
                                                                      "application/msword",
                                                                      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                                                                      "application/vnd.ms-excel",
                                                                      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                                                                      "text/csv",
                                                                      "image/jpeg",
                                                                      "image/png",
                                                                      "image/webp",
                                                                  ]

                                                            const validFiles = []
                                                            let errorMsg = ""

                                                            for (const file of files) {
                                                                // ✅ TYPE VALIDATION
                                                                if (
                                                                    !ALLOWED_FILE_TYPES.includes(
                                                                        file.type,
                                                                    )
                                                                ) {
                                                                    errorMsg =
                                                                        voiceiiTags("File type not allowed")
                                                                    break
                                                                }

                                                                // ✅ SIZE VALIDATION
                                                                if (file.size > MAX_FILE_SIZE) {
                                                                    errorMsg =
                                                                        voiceiiTags("File size must be less than 5MB")
                                                                    break
                                                                }

                                                                validFiles.push(file)
                                                            }

                                                            if (errorMsg) {
                                                                setErrors((prev) => ({
                                                                    ...prev,
                                                                    [field.key]: errorMsg,
                                                                }))
                                                                return
                                                            }

                                                            setErrors((prev) => {
                                                                const copy = { ...prev }
                                                                delete copy[field.key]
                                                                return copy
                                                            })

                                                            if (isMultiple) {
                                                                handleChange(field.key, [
                                                                    ...(value || []),
                                                                    ...validFiles,
                                                                ])
                                                            } else {
                                                                handleChange(
                                                                    field.key,
                                                                    validFiles[0] || null,
                                                                )
                                                            }
                                                        }}
                                                    />

                                                    <div className="e-dynamic-detail-form-modal-dropzone-content">
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            width="24"
                                                            height="24"
                                                            viewBox="0 0 24 24"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            strokeWidth="2"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            className="lucide lucide-upload"
                                                        >
                                                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                                            <polyline points="17 8 12 3 7 8"></polyline>
                                                            <line
                                                                x1="12"
                                                                x2="12"
                                                                y1="3"
                                                                y2="15"
                                                            ></line>
                                                        </svg>
                                                        <span className="e-dynamic-detail-form-modal-dropzone-text">
                                                            {isImageField
                                                                ? voiceiiTags("Drop images here or click to upload")
                                                                : voiceiiTags("Drop files here or click to upload")}
                                                        </span>
                                                        <span className="e-dynamic-detail-form-modal-dropzone-subtext">
                                                            {allowedFileText}
                                                        </span>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Preview/List */}
                                            {(() => {
                                                const files = Array.isArray(value)
                                                    ? value
                                                    : value
                                                      ? [value]
                                                      : []

                                                const isImageField =
                                                    field.type === "image" ||
                                                    field.type === "images"

                                                // Show empty state in view mode when no files
                                                if (files.length === 0 && isDisabled) {
                                                    return (
                                                        <div
                                                            className="e-dynamic-detail-form-modal-empty"
                                                            style={{
                                                                padding: "12px",
                                                                border: "1px solid #e5e5e5",
                                                                borderRadius: "4px",
                                                                backgroundColor: "#f9f9f9",
                                                                color: "#999",
                                                                fontSize: "14px",
                                                            }}
                                                        >
                                                            {isImageField
                                                                ? voiceiiTags("No images attached")
                                                                : voiceiiTags("No files attached")}
                                                        </div>
                                                    )
                                                }

                                                // Don't show anything in edit mode if no files (dropzone is above)
                                                if (files.length === 0) {
                                                    return null
                                                }

                                                return (
                                                    <div
                                                        className={
                                                            field.type === "image" ||
                                                            field.type === "images"
                                                                ? "e-dynamic-detail-form-modal-image-grid"
                                                                : "e-dynamic-detail-form-modal-file-list"
                                                        }
                                                    >
                                                        {files.map((file, i) => {
                                                            const isUrl = typeof file === "string"

                                                            const url = (() => {
                                                                const normalize = (raw) => {
                                                                    if (!raw) return ""
                                                                    const str = String(raw).trim()
                                                                    if (!str) return ""
                                                                    if (
                                                                        /^https?:\/\//i.test(str) ||
                                                                        str.startsWith("blob:") ||
                                                                        str.startsWith("data:")
                                                                    ) {
                                                                        return str
                                                                    }
                                                                    const withLeadingSlash =
                                                                        str.startsWith("/")
                                                                            ? str
                                                                            : `/${str}`
                                                                    return `${BASE_URL}${withLeadingSlash}`.replace(
                                                                        /([^:]\/)(\/)+/g,
                                                                        "$1",
                                                                    )
                                                                }

                                                                if (file instanceof File)
                                                                    return URL.createObjectURL(file)
                                                                if (typeof file === "string")
                                                                    return normalize(file)
                                                                return normalize(
                                                                    file?.url ||
                                                                        file?.path ||
                                                                        file?.file_path ||
                                                                        file?.filePath ||
                                                                        file?.location,
                                                                )
                                                            })()

                                                            const name = isUrl
                                                                ? decodeURIComponent(
                                                                      file
                                                                          .split("/")
                                                                          .pop()
                                                                          ?.split("?")[0] || "",
                                                                  )
                                                                : file?.name || "file"

                                                            const isImage =
                                                                field.type === "image" ||
                                                                field.type === "images"

                                                            return (
                                                                <div
                                                                    key={i}
                                                                    className={
                                                                        isImage
                                                                            ? "e-dynamic-detail-form-modal-image-item"
                                                                            : "e-dynamic-detail-form-modal-file-item"
                                                                    }
                                                                >
                                                                    {isImage ? (
                                                                        <>
                                                                            <a
                                                                                href={url}
                                                                                target="_blank"
                                                                                rel="noreferrer"
                                                                            >
                                                                                <img
                                                                                    src={url}
                                                                                    alt={name}
                                                                                    className="e-dynamic-detail-form-modal-image-preview"
                                                                                />
                                                                            </a>

                                                                            {!isDisabled && (
                                                                                <button
                                                                                    type="button"
                                                                                    className="e-dynamic-detail-form-modal-image-remove"
                                                                                    disabled={
                                                                                        !canManageRouteActions
                                                                                    }
                                                                                    onClick={() => {
                                                                                        if (
                                                                                            !canManageRouteActions
                                                                                        )
                                                                                            return

                                                                                        const updatedFiles =
                                                                                            files.filter(
                                                                                                (
                                                                                                    _,
                                                                                                    idx,
                                                                                                ) =>
                                                                                                    idx !==
                                                                                                    i,
                                                                                            )

                                                                                        handleChange(
                                                                                            field.key,
                                                                                            isMultiple
                                                                                                ? updatedFiles
                                                                                                : updatedFiles[0] ||
                                                                                                      null,
                                                                                        )
                                                                                    }}
                                                                                >
                                                                                    ✕
                                                                                </button>
                                                                            )}
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <div className="e-dynamic-detail-form-modal-file-info">
                                                                                📄
                                                                                <a
                                                                                    href={url}
                                                                                    target="_blank"
                                                                                    rel="noreferrer"
                                                                                    className="text-decoration-none"
                                                                                >
                                                                                    {name}
                                                                                </a>
                                                                            </div>

                                                                            {!isDisabled && (
                                                                                <button
                                                                                    type="button"
                                                                                    className="e-dynamic-detail-form-modal-file-remove"
                                                                                    disabled={
                                                                                        !canManageRouteActions
                                                                                    }
                                                                                    onClick={() => {
                                                                                        if (
                                                                                            !canManageRouteActions
                                                                                        )
                                                                                            return

                                                                                        const updatedFiles =
                                                                                            files.filter(
                                                                                                (
                                                                                                    _,
                                                                                                    idx,
                                                                                                ) =>
                                                                                                    idx !==
                                                                                                    i,
                                                                                            )

                                                                                        handleChange(
                                                                                            field.key,
                                                                                            isMultiple
                                                                                                ? updatedFiles
                                                                                                : updatedFiles[0] ||
                                                                                                      null,
                                                                                        )
                                                                                    }}
                                                                                >
                                                                                    ✕
                                                                                </button>
                                                                            )}
                                                                        </>
                                                                    )}
                                                                </div>
                                                            )
                                                        })}
                                                    </div>
                                                )
                                            })()}
                                        </div>
                                    )}
                                    {errors[field.key] && (
                                        <p className="e-dynamic-detail-form-modal-error">
                                            {errors[field.key]}
                                        </p>
                                    )}
                                </div>
                            )
                        })}
                        </div>

                        {/* Footer */}
                        <div className="e-dynamic-detail-form-modal-footer">
                            {isDisabled ? (
                                <div className="e-dynamic-detail-form-modal-view-actions">
                                    <FormPdfDownloadButton
                                        formItem={formItem}
                                        formValues={formState}
                                        meta={pdfDownloadMeta}
                                        onStart={() => setIsPdfDownloading(true)}
                                        onComplete={() => setIsPdfDownloading(false)}
                                        onError={() => setIsPdfDownloading(false)}
                                    />

                                    <button
                                        type="button"
                                        className="e-dynamic-detail-form-modal-btn e-dynamic-detail-form-modal-btn-secondary"
                                        onClick={onClose}
                                    >
                                        Close
                                    </button>
                                </div>
                            ) : (
                                !embedded && (
                                <>
                                    <button
                                        type="button"
                                        className="e-dynamic-detail-form-modal-btn e-dynamic-detail-form-modal-btn-secondary"
                                        onClick={onClose}
                                    >
                                        Cancel
                                    </button>

                                    <button
                                        disabled={
                                            isSubmitting || isFormInvalid || !canManageRouteActions
                                        }
                                        type="submit"
                                        className="e-dynamic-detail-form-modal-btn e-dynamic-detail-form-modal-btn-primary"
                                    >
                                        Save
                                    </button>
                                </>
                                )
                            )}
                        </div>
                    </form>
    )

    if (embedded) {
        return (
            <>
                <SimpleBackdropLoader showLoader={isSubmitting || isPdfDownloading} />
                <div className="e-dynamic-detail-form-modal-embedded">
                    {formContent}
                </div>
            </>
        )
    }

    return (
        <>
            <SimpleBackdropLoader showLoader={isSubmitting || isPdfDownloading} />
            <div className="e-dynamic-detail-form-modal-overlay" onClick={onClose}>
                <div className="e-dynamic-detail-form-modal" onClick={(e) => e.stopPropagation()}>
                    {/* Header */}
                    <div className="e-dynamic-detail-form-modal-header">
                        <h2 className="e-dynamic-detail-form-modal-title">{formItem?.title_key}</h2>
                        <button className="e-dynamic-detail-form-modal-close" onClick={onClose}>
                            ✕
                        </button>
                    </div>
                    {formContent}
                </div>
            </div>
        </>
    )
}

export default DynamicDetailFormModal